import { api, endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import { Button, Checkbox, Form, Input, InputNumber, Select, Spin } from "antd";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DeviceListOnDeviceForm from "./DeviceListOnDeviceForm";
import { flare } from "@flareapp/js";

const DeviceForm = ({ mode, data: editData = {} }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [eTariff, setETariff] = useState([]);
  const [waterTariffList, setWaterTariffList] = useState([]);

  // eslint-disable-next-line no-unused-vars
  const [isLimitOver, setIsLimitOver] = useState(false);
  const [warnMsg, setWarnMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [siteLocations, setSiteLocations] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [businessesList, setBusinessesList] = useState([]);
  const [state, setState] = useState({
    deviceType: mode === "create" ? "6682c35c9b4a5c76e2a0c65a" : "",
    site: undefined,
    business: undefined,
    devices: [],
    hasWaterTariff: false,
  });
  const { site, business, deviceType, devices } = state;

  const getElectricityTariff = async () => {
    try {
      let { results } = await api.get(endpoint.get_electricity_tariff, {
        params: { limit: 100, page: 1, sortBy: "position", orderBy: "ASC" },
      });
      const filtered = (results || []).filter(
        (item) => !item.name.toLowerCase().startsWith("tariff ")
      );
      const unique = Array.from(new Map(filtered.map((item) => [item.name, item])).values());
      setETariff(unique);
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const getWaterTariff = async () => {
    try {
      let { results } = await api.get(endpoint.get_water_tariff, {
        params: { limit: 100, page: 1, sortBy: "position", orderBy: "ASC" },
      });
      const unique = Array.from(new Map((results || []).map((item) => [item.name, item])).values());
      setWaterTariffList(unique);
    } catch (error) {
      flare.report(error);
      console.log(error);
    }
  };

  const getMySubscription = async () => {
    try {
      const { hasAccess, assignedPlan } = await api.get(endpoint.mySubscription, {
        params: {
          type: "device",
          unknown: true,
        },
      });
      if (mode !== "edit") {
        setIsLimitOver(!hasAccess);
        if (!hasAccess) {
          setWarnMsg(`${assignedPlan?.business?.name} (${assignedPlan?.business?.id})` + " has exceeded the limit for the maximum number of Device.");
        } else {
          setWarnMsg("");
        }
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };
  // console.log({ limitations, planDetails });
  const createDevice = async (values) => {
    setIsLoading(true);
    try {
      const res = await api.post(endpoint.get_devices, values);
      if (res) {
        await Swal.fire({
          title: "Done!",
          text: "New Device Created Successfully",
          icon: "success",
          timer: 2000,
          confirmButtonText: "OK",
        });
        navigate("/devices");
      }
    } catch (error) {
      flare.report(error);
      if (error.response) {
        await Swal.fire({
          title: "",
          text: error.response?.data?.message || "Something went wrong",
          icon: "error",
          timer: 2000,
          confirmButtonText: "OK",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateDevice = async (values) => {
    setIsLoading(true);
    try {
      const res = await api.put(endpoint.get_devices + editData?._id, values);
      if (res) {
        await Swal.fire({
          title: "Done!",
          text: "Device Updated Successfully",
          icon: "success",
          timer: 2000,
          confirmButtonText: "OK",
        });
        navigate(-1);
      }
    } catch (error) {
      flare.report(error);
      if (error.response) {
        await Swal.fire({
          title: "",
          text: error.response?.data?.message,
          icon: "error",
          timer: 2000,
          confirmButtonText: "OK",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  //htmlFor site location dropdown
  const getSiteLocations = async (business) => {
    setIsLoading(true);
    try {
      const { data } = await api.get(`/site-location`, { params: { business, limit: 10000 } });
      if (data) {
        const sites = data.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
        setSiteLocations(sites);
        return sites;
        // if (sites?.length) {
        //   setState((prev) => ({ ...prev, site: sites?.[0]?._id }));
        //   form.setFieldsValue({ site: sites?.[0]?._id });
        // }
      } else {
        setSiteLocations([]);
        return [];
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (editData?.business) {
      getSiteLocations(editData?.business?._id);
      getMySubscription(editData?.business?._id);
    }
  }, [editData?.business]);

  //device type handler
  const getDeviceTypes = async () => {
    try {
      const data = await api.get(`/device-type`);
      if (data) {
        const deviceTypeList = data.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
        setDeviceTypes(deviceTypeList);
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };
  const getBusiness = async () => {
    try {
      const data = await api.get(endpoint.business);
      setBusinessesList(data?.data);
      if (Array.isArray(data?.data) && data.data.length === 1) {
        form.setFieldsValue({ business: [data.data[0]?._id] });
        getSiteLocations([data.data[0]?._id]);
        getMySubscription([data.data[0]?._id]);
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  useEffect(() => {
    getDeviceTypes();
    getBusiness();
    getElectricityTariff();
    getWaterTariff();
  }, []);

  useEffect(() => {
    if (mode === "edit" && editData) {
      document.title = `${appConfig.name} - Edit Device`;
      setState((prev) => ({
        ...prev,
        ...editData,
        deviceType: editData?.deviceType?._id,
        site: editData.site?._id,
        business: editData.business?._id,
        devices: editData?.devices?.map((d) => d._id),
        installedArea: editData?.installedArea || "",
        electricityTariff: editData?.electricityTariff?._id,
        hasTariff: editData?.hasTariff,
        hasWaterTariff: editData?.hasWaterTariff,
        waterTariff: editData?.waterTariff?._id,
      }));
      form.setFieldsValue({
        hasTariff: editData?.hasTariff,
        electricityTariff: editData?.electricityTariff?._id,
        hasWaterTariff: editData?.hasWaterTariff,
        waterTariff: editData?.waterTariff?._id,
      });
    } else {
      document.title = `${appConfig.name} - Add Device`;
    }
  }, [mode, editData]);

  const onSelectDevice = (e) => {
    const data = Array.from(state.devices);
    const deviceId = e.target.value;
    if (data.includes(deviceId)) {
      data.splice(data.indexOf(deviceId), 1);
    } else {
      data.push(deviceId);
    }
    setState({ ...state, devices: data });
  };

  // antd
  const onFinish = async (values) => {
    if (mode === "create") {
      createDevice({ ...values, devices });
    } else if (mode === "edit") {
      updateDevice({ ...values, devices });
    }
    setIsLoading(false);
  };
  const onFinishFailed = (errorInfo) => {
    // eslint-disable-next-line no-console
    console.log("Failed:", errorInfo);
  };

  const onBusinessChange = async (value) => {
    // console.log({value})
    const sites = await getSiteLocations(value);
    await getMySubscription(value);
    if (sites?.length) {
      setState((prev) => ({ ...prev, site: sites?.[0]?._id, business: value }));
      form.setFieldsValue({ site: sites?.[0]?._id });
    } else {
      setState((prev) => ({ ...prev, business: value }));
      form.setFieldsValue({ site: [] });
    }
  };

  return (
    <Spin spinning={isLoading}>
      <div className="card p-3 m-md-3">
        <div>
          <label className="az-content-label">Manage Devices</label>
          <span className="d-block py-2">{mode === "edit" ? "Update Device" : "Add New Device"}</span>
        </div>
        <hr className="mt-0 mb-4" />
        <Form
          layout="vertical"
          name="users"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          form={form}
          initialValues={{
            ...editData,
            deviceType: editData?.deviceType?._id,
            site: editData.site?._id,
            business: editData.business?._id,
            devices: editData?.devices?.map((d) => d._id),
            electricityTariff: editData?.electricityTariff?._id,
            hasTariff: editData?.hasTariff,
          }}
        >
          <Row>
            <Col md={6}>
              <Form.Item label="Device Type" name="deviceType" className="w-100" rules={[{ required: true, message: "Select a Device Type" }]}>
                <Select
                  showSearch={true}
                  filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                  placeholder="Select Device Type"
                  options={[...(deviceTypes?.map((item) => ({ label: item.name, value: item._id })) || [])]}
                  onChange={(value) => setState((prev) => ({ ...prev, deviceType: value }))}
                  required
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Item
                label="Assign Business"
                name="business"
                className="w-100"
                rules={[
                  {
                    required: true,
                    message: "Select a Business",
                  },
                ]}
                extra={
                  warnMsg && (
                    <p
                      style={{
                        color: "red",
                        marginBottom: 0,
                        marginTop: "2px",
                      }}
                    >
                      Warning: {warnMsg}
                    </p>
                  )
                }
              >
                <Select
                  showSearch={true}
                  disabled={businessesList.length === 1}
                  filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                  placeholder="Select Business"
                  options={[...(businessesList?.map((item) => ({ label: `${item.name} (${item.id})`, value: item._id })) || [])]}
                  onChange={onBusinessChange}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Item label="Select Site Location" name="site" className="w-100" rules={[{ required: true, message: "Select Site Location" }]}>
                <Select
                  showSearch={true}
                  filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                  placeholder="Select Site Location"
                  options={[...siteLocations.map((item) => ({ label: item.name, value: item._id }))]}
                  onChange={(value) => setState((prev) => ({ ...prev, site: value }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Item label="Installed Area" name="installedArea" className="w-100">
                <Input placeholder="Enter Installed Area" />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Item label="Device Name" name="name" className="w-100" rules={[{ required: true, message: "Select a Device Name" }]}>
                <Input placeholder="Enter Device Name" />
              </Form.Item>
            </Col>
          </Row>

          {deviceTypes.some((item) => item._id === deviceType && item.name.toLowerCase() === "virtual device") ? (
            ""
          ) : (
            <>
              <Row>
                <Col md={6}>
                  <Form.Item label="Serial No" name="serial" className="w-100">
                    <Input placeholder="Enter Device Serial No" />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Item label="Device API Key" name="apiKey" className="w-100" rules={[{ required: true }]}>
                    <Input placeholder="Enter Device API key" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <Row>
            <Col md={6}>
              <Form.Item label="Multiply" name="multiply" className="w-100" rules={[{ required: true }]}>
                <Input type="number" defaultValue={1} placeholder="Enter Multiply Value" />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Item label="" name="hasTariff" valuePropName="checked" className="w-100">
                <Checkbox onChange={(e) => setState({ ...state, hasTariff: e.target.checked })}>Add Electricity Tariff</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          {state?.hasTariff && (
            <Row>
              <Col md={6}>
                <Form.Item
                  label="Electricity Tariff Category"
                  name="electricityTariff"
                  className="w-100"
                >
                  <Select
                    showSearch={true}
                    filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                    placeholder="Select Category"
                    options={[...eTariff.map((item) => ({ label: item.name, value: item._id }))]}
                    onChange={(value) => setState((prev) => ({ ...prev, electricityTariff: value }))}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row>
            <Col md={6}>
              <Form.Item label="" name="hasWaterTariff" valuePropName="checked" className="w-100">
                <Checkbox onChange={(e) => setState({ ...state, hasWaterTariff: e.target.checked })}>Add Water Tariff</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          {state?.hasWaterTariff && (
            <Row>
              <Col md={6}>
                <Form.Item
                  label="Water Tariff Category"
                  name="waterTariff"
                  className="w-100"
                >
                  <Select
                    showSearch={true}
                    filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                    placeholder="Select Category"
                    options={[...waterTariffList.map((item) => ({ label: item.name, value: item._id }))]}
                    onChange={(value) => setState((prev) => ({ ...prev, waterTariff: value }))}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row>
            <Col md={6}>
              <Form.Item label="" name="hasCustomOffline" valuePropName="checked" className="w-100">
                <Checkbox onChange={(e) => setState({ ...state, hasCustomOffline: e.target.checked })}>Enable Custom Offline Time</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          {state?.hasCustomOffline && (
            <Row>
              <Col md={6}>
                <Form.Item
                  label="Offline Time (in Minutes)"
                  name="offlineTime"
                  className="w-100"
                  rules={[{ required: true, message: "Enter Offline Time (in Minutes)" }]}
                >
                  <InputNumber
                    placeholder="Enter Offline Time in Minutes"
                    min={1}
                    step={1}
                    precision={0}
                    style={{ width: "100%" }}
                    controls={false}
                    onKeyDown={(e) => {
                      if (e.key === "Backspace" || e.key === "Delete" || e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "Tab") {
                        return e.target.value;
                      }
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          {deviceTypes.some((item) => item._id === deviceType && item.name === "Virtual Device") ? (
            <DeviceListOnDeviceForm site={site} business={business} devices={devices} onSelectDevice={onSelectDevice} />
          ) : (
            ""
          )}

          <div className="d-flex align-items-center justify-content-end gap-2 pt-2">
            <Button type="primary" htmlType="submit">
              {mode === "edit" ? "Update Device" : "Create Device"}
            </Button>
            <Button type="primary" className="bg-secondary" onClick={() => navigate(-1)} htmlType="button">
              Cancel
            </Button>
          </div>
        </Form>
      </div>
    </Spin>
  );
};

export default DeviceForm;

DeviceForm.propTypes = {
  mode: PropTypes.string,
  data: PropTypes.object,
  editData: PropTypes.object,
};
